// src/pages/ProductDetail.jsx
import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { useTenant } from "../context/TenantContext";
import SEO from "../components/Common/SEO";
import ProductDeliveryPromo from "../components/Product/ProductDeliveryPromo";
import api from "../services/api";
import toast from "react-hot-toast";
import { trackMarketingEvent } from "../utils/marketingTracking";
import {
  FiStar,
  FiTruck,
  FiShield,
  FiRefreshCw,
  FiMinus,
  FiPlus,
  FiShoppingCart,
  FiCheck,
  FiAlertCircle,
  FiUser,
  FiClock,
  FiEdit2,
  FiTrash2,
  FiTag,
  FiArrowUp,
  FiHeart,
  FiMaximize2,
  FiBatteryCharging,
  FiBluetooth,
  FiChevronLeft,
  FiChevronRight,
} from "react-icons/fi";
import LoadingSpinner from "../components/Common/LoadingSpinner";
import "../styles/product-detail.css";

const IGNORED_ATTRIBUTE_KEYS = new Set([
  "id",
  "variationid",
  "productid",
  "sku",
  "createdat",
  "updatedat",
  "created_at",
  "updated_at",
  "name",
  "price",
  "stockquantity",
  "isactive",
]);

const getVariationAttributes = (attributes) => {
  let source = attributes;
  if (typeof attributes === "string") {
    try {
      source = JSON.parse(attributes);
    } catch {
      source = {};
    }
  }
  if (!source || typeof source !== "object" || Array.isArray(source)) {
    return [];
  }
  return Object.entries(source)
    .map(([key, val]) => {
      const trimmedKey = String(key).trim();
      const lowerKey = trimmedKey.toLowerCase();
      if (IGNORED_ATTRIBUTE_KEYS.has(lowerKey)) return null;
      let formattedValue = "";
      if (Array.isArray(val)) {
        formattedValue = val.filter(Boolean).join(", ");
      } else if (val !== null && typeof val !== "object") {
        formattedValue = String(val).trim();
      }
      if (!trimmedKey || !formattedValue) return null;
      return [trimmedKey, formattedValue];
    })
    .filter(Boolean);
};

/**
 * Build a dynamic feature list from the actual product data.
 * Looks for battery, bluetooth, warranty, and shipping hints
 * inside Specification / Additional / Description fields.
 */
const buildProductFeatures = (product, displayProduct, activePromotion) => {
  const features = [];

  const specText = [
    product?.Specification,
    product?.Additional,
    product?.Description,
  ]
    .filter(Boolean)
    .join(" ");

  const findNumber = (regex) => {
    const match = specText.match(regex);
    return match ? match[1] : null;
  };

  // Battery life
  const battery = findNumber(/(\d+)\s*(?:hours?|hrs?)\s*battery/i);
  if (battery) {
    features.push({
      icon: "battery",
      primary: `Up to ${battery} hours`,
      secondary: "battery life",
    });
  }

  // Bluetooth version
  const bt = findNumber(/bluetooth\s*(\d+(?:\.\d+)?)/i);
  if (bt) {
    features.push({
      icon: "bluetooth",
      primary: `Bluetooth ${bt}`,
      secondary: "wireless",
    });
  }

  // Warranty
  const warranty = findNumber(/(\d+)\s*(?:year|yr)s?\s*warranty/i);
  if (warranty) {
    features.push({
      icon: "warranty",
      primary: `${warranty} Year${Number(warranty) > 1 ? "s" : ""}`,
      secondary: "warranty",
    });
  }

  // Shipping
  const shipping =
    findNumber(/free shipping.*?R(\d+)/i) || findNumber(/orders? over R(\d+)/i);
  if (shipping) {
    features.push({
      icon: "shipping",
      primary: "Free Shipping",
      secondary: `on orders over R${shipping}`,
    });
  }

  // Fallback: stock
  if (features.length === 0 && Number(displayProduct?.StockQuantity) > 0) {
    features.push({
      icon: "shipping",
      primary: "In Stock",
      secondary: `${displayProduct.StockQuantity} available`,
    });
  }

  // Fallback: promotion
  if (features.length < 4 && activePromotion) {
    features.push({
      icon: "tag",
      primary:
        activePromotion.DiscountType === "Percentage"
          ? `${activePromotion.DiscountValue}% OFF`
          : `Save R${activePromotion.DiscountValue}`,
      secondary: activePromotion.Code
        ? `Code: ${activePromotion.Code}`
        : "Limited time",
    });
  }

  return features.slice(0, 4);
};

const FeatureIcon = ({ type }) => {
  switch (type) {
    case "battery":
      return <FiBatteryCharging />;
    case "bluetooth":
      return <FiBluetooth />;
    case "warranty":
      return <FiShield />;
    case "shipping":
      return <FiTruck />;
    case "tag":
      return <FiTag />;
    default:
      return <FiCheck />;
  }
};

// ────────────────────────────────────────────────────────────
// Helper: format price in ZAR
// ────────────────────────────────────────────────────────────
const formatPrice = (value) =>
  `R${Number(value || 0).toLocaleString("en-ZA", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

// ────────────────────────────────────────────────────────────
// Helper: pick first image from a product row
// ────────────────────────────────────────────────────────────
const pickProductImage = (product) => {
  if (!product) return "https://via.placeholder.com/240?text=Product";
  if (Array.isArray(product.images) && product.images.length > 0) {
    const first = product.images[0];
    if (typeof first === "string") return first;
    if (first?.url) return first.url;
    if (first?.ImageUrl) return first.ImageUrl;
  }
  return (
    product.ImageUrl ||
    product.Images?.[0]?.url ||
    "https://via.placeholder.com/240?text=Product"
  );
};

// ────────────────────────────────────────────────────────────
// Helper: build a "signature" of a product's variation attributes
// (e.g. { color: [red, blue], size: [s, m, l] }) so we can measure
// how much two products' variations overlap — used to weight
// "related products" toward items that come in similar options.
// ────────────────────────────────────────────────────────────
const getVariationSignature = (variations) => {
  const keys = new Set();
  const values = new Set();
  if (!Array.isArray(variations)) return { keys, values };

  variations.forEach((variation) => {
    getVariationAttributes(variation?.Attributes).forEach(([key, value]) => {
      keys.add(key.trim().toLowerCase());
      String(value)
        .split(",")
        .map((v) => v.trim().toLowerCase())
        .filter(Boolean)
        .forEach((v) => values.add(v));
    });
  });

  return { keys, values };
};

// ────────────────────────────────────────────────────────────
// Horizontal Related Products Carousel
// ────────────────────────────────────────────────────────────
const RelatedProductsCarousel = ({
  products,
  loading,
  addToCart,
  renderStars,
}) => {
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollButtons = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setCanScrollLeft(scrollLeft > 4);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 4);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateScrollButtons();
    el.addEventListener("scroll", updateScrollButtons, { passive: true });
    const resizeObserver = new ResizeObserver(updateScrollButtons);
    resizeObserver.observe(el);
    return () => {
      el.removeEventListener("scroll", updateScrollButtons);
      resizeObserver.disconnect();
    };
  }, [products, updateScrollButtons]);

  const scrollByAmount = (direction) => {
    const el = scrollRef.current;
    if (!el) return;
    const card = el.querySelector(".related-card");
    const cardWidth = card ? card.offsetWidth + 16 : 260;
    el.scrollBy({ left: direction * cardWidth * 2, behavior: "smooth" });
  };

  if (loading) {
    return <div className="related-carousel-status">Loading products...</div>;
  }

  if (!products || products.length === 0) {
    return (
      <div className="related-carousel-status">No related products found.</div>
    );
  }

  return (
    <div className="related-carousel-wrapper">
      <button
        type="button"
        className="related-carousel-nav prev"
        onClick={() => scrollByAmount(-1)}
        disabled={!canScrollLeft}
        aria-label="Scroll related products left"
      >
        <FiChevronLeft />
      </button>

      <div className="related-carousel-track" ref={scrollRef}>
        {products.map((relatedProduct) => {
          const price = parseFloat(relatedProduct.SellingPrice) || 0;
          const originalPrice = parseFloat(relatedProduct.CostPrice) || 0;
          const hasDiscount = originalPrice > price;
          const discountPercent = hasDiscount
            ? Math.round(((originalPrice - price) / originalPrice) * 100)
            : 0;

          // Pull variations from the related product if present
          const variations = Array.isArray(relatedProduct.variations)
            ? relatedProduct.variations
            : [];

          const avgRating = relatedProduct.reviewStats?.rating || 0;
          const totalReviews = relatedProduct.reviewStats?.count || 0;

          return (
            <article key={relatedProduct.ProductID} className="related-card">
              <Link
                to={`/product/${relatedProduct.ProductID}`}
                className="related-card-image-link"
              >
                <img
                  src={pickProductImage(relatedProduct)}
                  alt={relatedProduct.Name}
                  loading="lazy"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src =
                      "https://via.placeholder.com/240?text=Product";
                  }}
                />
                {hasDiscount && (
                  <span className="related-card-discount">
                    -{discountPercent}%
                  </span>
                )}
              </Link>

              <div className="related-card-body">
                {relatedProduct.CategoryName && (
                  <span className="related-card-category">
                    {relatedProduct.CategoryName}
                  </span>
                )}

                <Link
                  to={`/product/${relatedProduct.ProductID}`}
                  className="related-card-name"
                  title={relatedProduct.Name}
                >
                  {relatedProduct.Name}
                </Link>

                <div className="related-card-rating">
                  <div className="related-card-stars">
                    {renderStars(avgRating, false, "small")}
                  </div>
                  <span className="related-card-rating-count">
                    ({totalReviews})
                  </span>
                </div>

                {variations.length > 0 && (
                  <div className="related-card-variations">
                    {variations.slice(0, 4).map((variation) => (
                      <span
                        key={variation.VariationID || variation.Name}
                        className="related-card-variation-chip"
                        title={variation.Name}
                      >
                        {variation.Name}
                      </span>
                    ))}
                    {variations.length > 4 && (
                      <span className="related-card-variation-chip more">
                        +{variations.length - 4}
                      </span>
                    )}
                  </div>
                )}

                <div className="related-card-price-row">
                  <span className="related-card-price">
                    {formatPrice(price)}
                  </span>
                  {hasDiscount && (
                    <span className="related-card-old-price">
                      {formatPrice(originalPrice)}
                    </span>
                  )}
                </div>

                <div className="related-card-actions">
                  <button
                    type="button"
                    className="related-card-add-btn"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      addToCart(relatedProduct.ProductID, 1, {
                        name: relatedProduct.Name,
                        price,
                        imageUrl: pickProductImage(relatedProduct),
                        sku: relatedProduct.SKU,
                      });
                    }}
                    aria-label={`Add ${relatedProduct.Name} to cart`}
                  >
                    <FiShoppingCart /> Add
                  </button>
                  <button
                    type="button"
                    className="related-card-wishlist"
                    aria-label="Add to wishlist"
                  >
                    <FiHeart />
                  </button>
                </div>
              </div>
            </article>
          );
        })}
      </div>

      <button
        type="button"
        className="related-carousel-nav next"
        onClick={() => scrollByAmount(1)}
        disabled={!canScrollRight}
        aria-label="Scroll related products right"
      >
        <FiChevronRight />
      </button>
    </div>
  );
};

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addToCart } = useCart();
  const { tenant } = useTenant();
  const queryClient = useQueryClient();
  const reviewSectionRef = useRef(null);

  const mainAddToCartRef = useRef(null);
  const [showStickyBarAbove, setShowStickyBarAbove] = useState(false);
  const [footerVisible, setFooterVisible] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);

  const [quantity, setQuantity] = useState(1);
  const [activeImage, setActiveImage] = useState(0);
  const [hoverImage, setHoverImage] = useState(null);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewComment, setReviewComment] = useState("");
  const [hoverRating, setHoverRating] = useState(0);
  const [editingReview, setEditingReview] = useState(null);
  const [selectedVariationId, setSelectedVariationId] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [isWishlisted, setIsWishlisted] = useState(false);

  // Fetch product details
  const {
    data: productData,
    isLoading: productLoading,
    error: productError,
  } = useQuery(
    ["product", id],
    async () => {
      const response = await api.get(`api/products/${id}`);
      return response.data;
    },
    { enabled: !!id },
  );

  // Fetch active promotion
  const { data: promotionData } = useQuery(
    ["product-promotions", id],
    async () => {
      try {
        const response = await api.get(`/api/promotions/product/${id}/active`);
        return response.data;
      } catch (err) {
        if (productData?.product?.Promotions) {
          const now = new Date();
          const activePromotion = productData.product.Promotions.find((p) => {
            const start = new Date(p.StartDate);
            const end = new Date(p.EndDate);
            return p.IsActive && now >= start && now <= end;
          });
          return activePromotion || null;
        }
        return null;
      }
    },
    { enabled: !!id && !!productData },
  );

  // Fetch reviews
  const { data: reviewsData, isLoading: reviewsLoading } = useQuery(
    ["product-reviews", id],
    async () => {
      try {
        const response = await api.get(`/reviews/product/${id}`);
        return response.data;
      } catch (err) {
        console.log("Reviews endpoint will be available soon");
        return { reviews: [], averageRating: 0, totalReviews: 0 };
      }
    },
    { enabled: !!id },
  );

  const product = productData?.product || productData;

  useEffect(() => {
    const trackProductView = () => {
      if (!product?.ProductID) return;
      trackMarketingEvent("view_item", {
        productId: String(product.ProductID),
        productName: product.Name || "Product",
        value: Number(product.SellingPrice || 0),
      });
    };
    trackProductView();
    window.addEventListener("cookieConsentUpdated", trackProductView);
    return () =>
      window.removeEventListener("cookieConsentUpdated", trackProductView);
  }, [product?.ProductID, product?.Name, product?.SellingPrice]);

  // ────────────────────────────────────────────────────────────
  // Related products — two-pool strategy:
  //  1) Same-category pool, fetched with the API's own categoryId
  //     filter (instead of hoping matches show up in a generic
  //     "latest N products" batch).
  //  2) A fallback pool (any category) used only to top up the list
  //     if the category doesn't have enough other products.
  // Both pools are then scored by category + variation overlap +
  // price proximity so the *most* related items surface first.
  // ────────────────────────────────────────────────────────────
  const { data: sameCategoryProducts, isLoading: sameCategoryLoading } =
    useQuery(
      ["related-products-category", product?.CategoryID, product?.ProductID],
      async () => {
        const response = await api.get("/api/products", {
          params: {
            categoryId: product.CategoryID,
            excludeProductId: product.ProductID,
            limit: 24,
          },
        });
        return response.data?.products || [];
      },
      { enabled: !!product?.CategoryID && !!product?.ProductID },
    );

  const { data: fallbackProducts, isLoading: fallbackLoading } = useQuery(
    ["related-products-fallback", product?.ProductID],
    async () => {
      const response = await api.get("/api/products", {
        params: {
          excludeProductId: product?.ProductID,
          limit: 24,
          sort: "CreatedAt",
          order: "DESC",
        },
      });
      return response.data?.products || [];
    },
    { enabled: !!product?.ProductID, staleTime: 5 * 60 * 1000 },
  );

  const relatedProductsLoading = product?.CategoryID
    ? sameCategoryLoading
    : fallbackLoading;

  // Review mutations
  const submitReview = useMutation(
    async (reviewData) => {
      const response = await api.post("api/reviews", reviewData);
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["product-reviews", id]);
        queryClient.invalidateQueries(["homepage-review-summary"]);
        setShowReviewForm(false);
        setReviewComment("");
        setReviewRating(5);
        toast.success("Review submitted successfully!");
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || "Failed to submit review");
      },
    },
  );

  const updateReview = useMutation(
    async ({ reviewId, rating, comment }) => {
      const response = await api.put(`api/reviews/${reviewId}`, {
        rating,
        comment,
      });
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["product-reviews", id]);
        queryClient.invalidateQueries(["homepage-review-summary"]);
        setEditingReview(null);
        setShowReviewForm(false);
        toast.success("Review updated successfully!");
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || "Failed to update review");
      },
    },
  );

  const deleteReview = useMutation(
    async (reviewId) => {
      const response = await api.delete(`/reviews/${reviewId}`);
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["product-reviews", id]);
        queryClient.invalidateQueries(["homepage-review-summary"]);
        toast.success("Review deleted successfully!");
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || "Failed to delete review");
      },
    },
  );

  const activePromotion =
    promotionData?.promotion ||
    product?.Promotions?.find((p) => {
      const now = new Date();
      const start = new Date(p.StartDate);
      const end = new Date(p.EndDate);
      return p.IsActive && now >= start && now <= end;
    });

  const reviews = reviewsData?.reviews || [];
  const averageRating =
    reviewsData?.averageRating ?? product?.reviewStats?.rating ?? 0;
  const totalReviews =
    reviewsData?.totalReviews ?? product?.reviewStats?.count ?? 0;
  const productVariations = Array.isArray(product?.variations)
    ? product.variations
    : [];
  const selectedVariation =
    productVariations.find(
      (variation) => variation.VariationID === selectedVariationId,
    ) || null;

  // IMPORTANT: when no variation is selected, main product info is used.
  // When a variation IS selected, the variation becomes the display product,
  // and the main product's own fields are still used as fallback.
  const displayProduct = selectedVariation
    ? { ...product, ...selectedVariation }
    : product;

  const relatedProducts = React.useMemo(() => {
    if (!product) return [];

    const currentSignature = getVariationSignature(productVariations);

    // Merge both pools, same-category first so ties naturally favor it,
    // then de-duplicate by ProductID (a product could theoretically
    // appear in both pools) and drop the current product just in case.
    const pool = [...(sameCategoryProducts || []), ...(fallbackProducts || [])];

    const seen = new Set([product.ProductID]);
    const deduped = [];
    for (const item of pool) {
      if (!item?.ProductID || seen.has(item.ProductID)) continue;
      seen.add(item.ProductID);
      deduped.push(item);
    }

    const currentPrice = parseFloat(product.SellingPrice) || 0;

    const scored = deduped.map((item) => {
      let score = 0;

      // Category match is the strongest signal.
      if (product.CategoryID && item.CategoryID === product.CategoryID) {
        score += 10;
      }

      // Same brand, if the schema has one.
      if (
        product.BrandName &&
        item.BrandName &&
        item.BrandName === product.BrandName
      ) {
        score += 2;
      }

      // Variation overlap: products that come in the same *kinds* of
      // options (e.g. both have a "color" variation) score higher, and
      // sharing actual option values (e.g. both come in "black") scores
      // higher still.
      const itemSignature = getVariationSignature(item.variations);
      let sharedKeys = 0;
      currentSignature.keys.forEach((key) => {
        if (itemSignature.keys.has(key)) sharedKeys += 1;
      });
      let sharedValues = 0;
      currentSignature.values.forEach((value) => {
        if (itemSignature.values.has(value)) sharedValues += 1;
      });
      score += sharedKeys * 3 + sharedValues * 1.5;

      // Price proximity as a soft tie-breaker (closer price = more
      // "comparable" item), capped so it never outweighs the signals
      // above.
      const itemPrice = parseFloat(item.SellingPrice) || 0;
      if (currentPrice > 0) {
        const priceDiffRatio =
          Math.abs(itemPrice - currentPrice) / currentPrice;
        score += Math.max(0, 2 - priceDiffRatio * 2);
      }

      return { item, score };
    });

    scored.sort((a, b) => b.score - a.score);

    return scored.slice(0, 12).map((entry) => entry.item);
  }, [product, productVariations, sameCategoryProducts, fallbackProducts]);

  const calculateDiscountedPrice = (originalPrice, promotion) => {
    if (!promotion) return null;
    const discountValue = parseFloat(promotion.DiscountValue) || 0;
    if (promotion.DiscountType === "Percentage") {
      return Math.round(originalPrice * (1 - discountValue / 100) * 100) / 100;
    } else if (promotion.DiscountType === "FixedAmount") {
      return Math.max(
        0,
        Math.round((originalPrice - discountValue) * 100) / 100,
      );
    }
    return null;
  };

  const originalPrice = parseFloat(displayProduct?.SellingPrice) || 0;
  const discountedPrice = activePromotion
    ? calculateDiscountedPrice(originalPrice, activePromotion)
    : null;
  const finalPrice = discountedPrice !== null ? discountedPrice : originalPrice;
  const discountApplied =
    discountedPrice !== null ? originalPrice - discountedPrice : 0;

  const productImages = React.useMemo(() => {
    if (!product) return [];
    const collectedImages = [];

    const addImage = (value) => {
      if (!value) return;
      if (Array.isArray(value)) {
        value.forEach(addImage);
        return;
      }
      if (typeof value === "object" && value !== null) {
        if (value.url || value.ImageUrl) {
          collectedImages.push(value.url || value.ImageUrl);
        }
        return;
      }
      if (typeof value !== "string") return;
      const trimmed = value.trim();
      if (!trimmed) return;
      if (trimmed.startsWith("[")) {
        try {
          const parsed = JSON.parse(trimmed);
          if (Array.isArray(parsed)) {
            parsed.forEach(addImage);
            return;
          }
        } catch (error) {
          console.warn("Could not parse image array string", error);
        }
      }
      if (trimmed.includes("|")) {
        trimmed.split("|").forEach(addImage);
        return;
      }
      if (trimmed.includes(",") && trimmed.includes("http")) {
        const parts = trimmed.split(",").map((part) => part.trim());
        if (parts.length > 1) {
          parts.forEach(addImage);
          return;
        }
      }
      collectedImages.push(trimmed);
    };

    // If a variation is selected, use its images first
    if (selectedVariation?.VariationImages) {
      addImage(selectedVariation.VariationImages);
    }
    if (
      collectedImages.length === 0 &&
      product.images &&
      Array.isArray(product.images)
    ) {
      product.images.forEach(addImage);
    }
    if (collectedImages.length === 0) {
      addImage(displayProduct.ImageUrl);
      addImage(product.Images);
      addImage(product.ImageUrls);
      addImage(product.AdditionalImages);
      addImage(product.ImageData);
    }
    return collectedImages.filter(
      (image, index, self) => image && self.indexOf(image) === index,
    );
  }, [product, selectedVariation, displayProduct]);

  // Reset state when product id changes
  useEffect(() => {
    setActiveImage(0);
    setHoverImage(null);
    setSelectedVariationId(null);
    setActiveTab("overview");
  }, [id, product?.ProductID]);

  // Sticky bar
  useEffect(() => {
    if (productLoading || !product) return;
    const target = mainAddToCartRef.current;
    if (!target) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        const isBelowViewport =
          entry.boundingClientRect.top > window.innerHeight;
        setShowStickyBarAbove(isBelowViewport);
      },
      { threshold: 0 },
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [productLoading, product]);

  // ============================================================
  // Scroll-to-top visibility — reliable scroll listener
  // Works on BOTH desktop and mobile, regardless of active tab.
  // ============================================================
  useEffect(() => {
    const getScrollTop = () => {
      const scrollContainer = document.querySelector(".layout");
      if (scrollContainer && scrollContainer.scrollTop > 0) {
        return scrollContainer.scrollTop;
      }
      return (
        window.scrollY ||
        window.pageYOffset ||
        document.documentElement.scrollTop ||
        document.body.scrollTop ||
        0
      );
    };

    const handleScroll = () => {
      setShowScrollTop(getScrollTop() > 400);
    };

    // Initial check (in case page loads already scrolled)
    handleScroll();

    const scrollContainer = document.querySelector(".layout");
    window.addEventListener("scroll", handleScroll, { passive: true });
    document.addEventListener("scroll", handleScroll, { passive: true });
    if (scrollContainer) {
      scrollContainer.addEventListener("scroll", handleScroll, {
        passive: true,
      });
    }

    return () => {
      window.removeEventListener("scroll", handleScroll);
      document.removeEventListener("scroll", handleScroll);
      if (scrollContainer) {
        scrollContainer.removeEventListener("scroll", handleScroll);
      }
    };
  }, []);

  // Footer intersection
  useEffect(() => {
    const footer = document.querySelector("footer");
    if (!footer) return;
    const observer = new IntersectionObserver(
      ([entry]) => setFooterVisible(entry.isIntersecting),
      { threshold: 0, rootMargin: "0px" },
    );
    observer.observe(footer);
    return () => observer.disconnect();
  }, []);

  const handleAutoScrollUp = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const scrollContainer = document.querySelector(".layout");
    if (scrollContainer && scrollContainer.scrollTop > 0) {
      scrollContainer.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    } else {
      window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    }
  };

  const userReview = user
    ? reviews.find((r) => r.UserID === user.userId)
    : null;

  const handleWriteReviewClick = () => {
    setShowReviewForm(true);
    setActiveTab("reviews");
    setTimeout(() => {
      if (reviewSectionRef.current) {
        reviewSectionRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    }, 100);
  };

  const handleQuantityChange = (type) => {
    if (
      type === "increase" &&
      quantity < (displayProduct?.StockQuantity || 10)
    ) {
      setQuantity(quantity + 1);
    } else if (type === "decrease" && quantity > 1) {
      setQuantity(quantity - 1);
    }
  };

  const buildCartPayload = () => {
    let price = parseFloat(displayProduct.SellingPrice) || 0;
    let discount = 0;
    if (activePromotion) {
      const discountValue = parseFloat(activePromotion.DiscountValue) || 0;
      if (activePromotion.DiscountType === "Percentage") {
        price = Math.round(price * (1 - discountValue / 100) * 100) / 100;
      } else if (activePromotion.DiscountType === "FixedAmount") {
        price = Math.max(0, Math.round((price - discountValue) * 100) / 100);
      }
      discount = (parseFloat(displayProduct.SellingPrice) || 0) - price;
    }
    return {
      name: selectedVariation
        ? `${product.Name} - ${selectedVariation.Name}`
        : product.Name,
      price,
      originalPrice: parseFloat(displayProduct.SellingPrice) || 0,
      imageUrl: displayProduct.ImageUrl,
      sku: displayProduct.SKU,
      variationId: selectedVariation?.VariationID,
      variationAttributes: selectedVariation?.Attributes,
      promotion: activePromotion,
      discountApplied: discount,
    };
  };

  const handleAddToCart = async () => {
    if (!product) return;
    try {
      await addToCart(product.ProductID, quantity, buildCartPayload());
    } catch (error) {
      console.error("Error adding to cart:", error);
      toast.error("Failed to add to cart");
    }
  };

  const handleBuyNow = async () => {
    if (!product) return;
    try {
      await addToCart(product.ProductID, quantity, buildCartPayload());
      navigate("/checkout");
    } catch (error) {
      console.error("Error adding to cart:", error);
      toast.error("Failed to add to cart");
    }
  };

  const handleSubmitReview = (e) => {
    e.preventDefault();
    if (!user) {
      toast.error("Please login to leave a review");
      navigate("/login");
      return;
    }
    if (editingReview) {
      updateReview.mutate({
        reviewId: editingReview.ReviewID,
        rating: reviewRating,
        comment: reviewComment,
      });
    } else {
      submitReview.mutate({
        productId: id,
        rating: reviewRating,
        comment: reviewComment,
      });
    }
  };

  const handleEditReview = (review) => {
    setEditingReview(review);
    setReviewRating(review.Rating);
    setReviewComment(review.Comment);
    setShowReviewForm(true);
    setActiveTab("reviews");
    setTimeout(() => {
      if (reviewSectionRef.current) {
        reviewSectionRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    }, 100);
  };

  const handleDeleteReview = (reviewId) => {
    if (window.confirm("Are you sure you want to delete your review?")) {
      deleteReview.mutate(reviewId);
    }
  };

  const renderStars = (ratingValue, interactive = false, size = "medium") => {
    const starSize = size === "small" ? 16 : size === "large" ? 24 : 20;
    return (
      <div className={`stars-container ${interactive ? "interactive" : ""}`}>
        {[1, 2, 3, 4, 5].map((star) => (
          <FiStar
            key={star}
            size={starSize}
            className={`star ${
              star <=
              (interactive
                ? hoverRating || reviewRating
                : Math.round(ratingValue))
                ? "filled"
                : ""
            }`}
            onClick={() => interactive && setReviewRating(star)}
            onMouseEnter={() => interactive && setHoverRating(star)}
            onMouseLeave={() => interactive && setHoverRating(0)}
          />
        ))}
      </div>
    );
  };

  const handleThumbnailClick = (index) => {
    setActiveImage(index);
    setHoverImage(null);
  };
  const handleThumbnailHover = (index) => setHoverImage(index);
  const handleThumbnailLeave = () => setHoverImage(null);

  if (productLoading) return <LoadingSpinner />;
  if (productError || !product) {
    return (
      <div className="product-not-found">
        <FiAlertCircle size={48} />
        <h2>Product Not Found</h2>
        <p>The product you're looking for doesn't exist or has been removed.</p>
        <Link to="/shop" className="back-to-shop">
          Back to Shop
        </Link>
      </div>
    );
  }

  const isInStock = Number(displayProduct.StockQuantity) > 0;
  const discount =
    displayProduct.SellingPrice < displayProduct.CostPrice
      ? Math.round(
          ((displayProduct.CostPrice - displayProduct.SellingPrice) /
            displayProduct.CostPrice) *
            100,
        )
      : 0;

  const promotionDiscountPercent =
    activePromotion && activePromotion.DiscountType === "Percentage"
      ? Math.round(parseFloat(activePromotion.DiscountValue))
      : 0;

  const galleryImages =
    productImages.length > 0
      ? productImages
      : ["https://via.placeholder.com/500"];

  const displayImageIndex = hoverImage !== null ? hoverImage : activeImage;
  const currentImage = galleryImages[displayImageIndex] || galleryImages[0];

  const handlePrevImage = () => {
    setActiveImage((prev) =>
      prev === 0 ? galleryImages.length - 1 : prev - 1,
    );
    setHoverImage(null);
  };

  const handleNextImage = () => {
    setActiveImage((prev) =>
      prev === galleryImages.length - 1 ? 0 : prev + 1,
    );
    setHoverImage(null);
  };

  const mainProductImage =
    product.ImageUrl ||
    product.Images?.[0]?.url ||
    product.ImageUrls?.[0] ||
    galleryImages[0] ||
    "https://via.placeholder.com/64";

  const features = buildProductFeatures(
    product,
    displayProduct,
    activePromotion,
  );

  return (
    <div className="product-detail-page">
      <div className="container">
        {/* Breadcrumb */}
        <nav className="breadcrumb">
          <Link to="/">Home</Link>
          <FiChevronRight className="crumb-sep" />
          <Link to="/shop">Shop</Link>
          {product.CategoryName && (
            <>
              <FiChevronRight className="crumb-sep" />
              <Link to={`/shop?category=${product.CategoryID}`}>
                {product.CategoryName}
              </Link>
            </>
          )}
          <FiChevronRight className="crumb-sep" />
          <span className="current">{product.Name}</span>
        </nav>

        {/* Product Main Section */}
        <div className="product-main">
          {/* LEFT: Gallery */}
          <div className="product-gallery-left">
            <div className="thumbnail-list-vertical">
              {galleryImages.slice(0, 5).map((img, idx) => (
                <button
                  key={`${img}-${idx}`}
                  type="button"
                  className={`thumbnail-vertical ${
                    activeImage === idx ? "active" : ""
                  }`}
                  onClick={() => handleThumbnailClick(idx)}
                  onMouseEnter={() => handleThumbnailHover(idx)}
                  onMouseLeave={handleThumbnailLeave}
                  aria-label={`View image ${idx + 1}`}
                >
                  <img
                    src={img}
                    alt={`Product view ${idx + 1}`}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = "https://via.placeholder.com/120";
                    }}
                  />
                </button>
              ))}
            </div>

            <div className="gallery-main-wrapper">
              <div className="main-image-large">
                <button
                  type="button"
                  className="gallery-nav prev"
                  onClick={handlePrevImage}
                  aria-label="Previous image"
                >
                  <FiChevronLeft />
                </button>
                <img
                  src={currentImage}
                  alt={product.Name}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "https://via.placeholder.com/500";
                  }}
                />
                <button
                  type="button"
                  className="gallery-nav next"
                  onClick={handleNextImage}
                  aria-label="Next image"
                >
                  <FiChevronRight />
                </button>
                <button
                  type="button"
                  className="wishlist-float"
                  onClick={() => setIsWishlisted((v) => !v)}
                  aria-label="Add to wishlist"
                >
                  <FiHeart fill={isWishlisted ? "currentColor" : "none"} />
                </button>
                <button
                  type="button"
                  className="fullscreen-btn"
                  aria-label="View fullscreen"
                >
                  <FiMaximize2 />
                </button>
                {activePromotion && (
                  <div className="discount-badge promotion-badge-large">
                    <FiTag />{" "}
                    {promotionDiscountPercent > 0
                      ? `${promotionDiscountPercent}% OFF`
                      : "SALE"}
                  </div>
                )}
                {discount > 0 && !activePromotion && (
                  <div className="discount-badge">-{discount}%</div>
                )}
              </div>
            </div>
          </div>

          {/* RIGHT: Product Info */}
          <div className="product-info">
            {product.BrandName && (
              <div className="product-brand">{product.BrandName}</div>
            )}

            <h1 className="product-title">
              {displayProduct.Name || product.Name}
            </h1>
            {(product.SupplierName || product.SupplierLocation) && (
              <div className="product-supplier-info">
                {product.SupplierName && <span>Supplier: {product.SupplierName}</span>}
                {product.SupplierLocation && <span>Location: {product.SupplierLocation}</span>}
              </div>
            )}

            <div className="product-rating-summary">
              <div className="rating-stars">
                {renderStars(averageRating, false, "small")}
              </div>
              <span className="rating-value">{averageRating.toFixed(1)}</span>
              <button
                type="button"
                className="rating-count"
                onClick={handleWriteReviewClick}
              >
                ({totalReviews} reviews)
              </button>
            </div>

            {/* Price */}
            <div className="product-price">
              {activePromotion ? (
                <>
                  <span className="current-price promotion-price">
                    {formatPrice(finalPrice)}
                  </span>
                  <span className="original-price">
                    {formatPrice(originalPrice)}
                  </span>
                  <span className="saved-badge">
                    Save R{discountApplied.toFixed(2)}
                  </span>
                </>
              ) : discount > 0 ? (
                <>
                  <span className="current-price">
                    {formatPrice(originalPrice)}
                  </span>
                  <span className="original-price">
                    {formatPrice(displayProduct.CostPrice)}
                  </span>
                  <span className="saved-badge">
                    Save R
                    {(
                      displayProduct.CostPrice - displayProduct.SellingPrice
                    ).toFixed(2)}
                  </span>
                </>
              ) : (
                <span className="current-price">
                  {formatPrice(originalPrice)}
                </span>
              )}
            </div>
            <ProductDeliveryPromo />

            <p className="product-short-description">
              {product.Description || "No description available."}
            </p>

            {/* Variations */}
            {productVariations.length > 0 && (
              <div className="product-variations-block">
                <div className="variations-label">
                  <span className="variations-label-key">
                    {(() => {
                      const first = productVariations[0];
                      if (!first) return "Options";
                      const attrs = getVariationAttributes(first.Attributes);
                      if (attrs.length > 0) return attrs[0][0];
                      return "Options";
                    })()}
                    :
                  </span>
                  <span className="variations-label-value">
                    {selectedVariation
                      ? selectedVariation.Name
                      : "Main Product"}
                  </span>
                </div>
                <div className="variation-swatches">
                  {/* Main product / default swatch — always first, allows going back */}
                  <button
                    type="button"
                    className={`variation-swatch ${
                      selectedVariationId === null ? "selected" : ""
                    }`}
                    onClick={() => {
                      setSelectedVariationId(null);
                      setActiveImage(0);
                      setQuantity(1);
                    }}
                    title="Main product"
                  >
                    <div className="swatch-image">
                      <img
                        src={mainProductImage}
                        alt="Main product"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = "https://via.placeholder.com/64";
                        }}
                      />
                    </div>
                    <span className="swatch-name">Default</span>
                  </button>

                  {productVariations.map((variation) => {
                    const isSelected =
                      selectedVariationId === variation.VariationID;
                    const img =
                      variation.ImageUrl ||
                      variation.VariationImages?.[0]?.ImageUrl ||
                      "https://via.placeholder.com/64";
                    return (
                      <button
                        key={variation.VariationID}
                        type="button"
                        className={`variation-swatch ${
                          isSelected ? "selected" : ""
                        }`}
                        onClick={() => {
                          setSelectedVariationId(variation.VariationID);
                          setActiveImage(0);
                          setQuantity(1);
                        }}
                        title={variation.Name}
                      >
                        <div className="swatch-image">
                          <img
                            src={img}
                            alt={variation.Name}
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = "https://via.placeholder.com/64";
                            }}
                          />
                        </div>
                        <span className="swatch-name">{variation.Name}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Quantity + Add to Cart */}
            <div className="purchase-block" ref={mainAddToCartRef}>
              <div className="quantity-block">
                <label className="quantity-label">Quantity</label>
                <div className="quantity-selector">
                  <button
                    type="button"
                    onClick={() => handleQuantityChange("decrease")}
                    disabled={quantity <= 1}
                    aria-label="Decrease quantity"
                  >
                    <FiMinus />
                  </button>
                  <span>{quantity}</span>
                  <button
                    type="button"
                    onClick={() => handleQuantityChange("increase")}
                    disabled={
                      !isInStock ||
                      quantity >= (displayProduct.StockQuantity || 10)
                    }
                    aria-label="Increase quantity"
                  >
                    <FiPlus />
                  </button>
                </div>
              </div>

              <button
                type="button"
                className="add-to-cart-btn primary"
                onClick={handleAddToCart}
                disabled={!isInStock}
              >
                <FiShoppingCart />
                Add to Cart
              </button>

              <button
                type="button"
                className="wishlist-inline-btn"
                onClick={() => setIsWishlisted((v) => !v)}
                aria-label="Toggle wishlist"
              >
                <FiHeart fill={isWishlisted ? "currentColor" : "none"} />
              </button>
            </div>

            <button
              type="button"
              className="buy-now-btn"
              onClick={handleBuyNow}
              disabled={!isInStock}
              style={{ backgroundColor: tenant?.PrimaryColor }}
            >
              Buy Now
            </button>

            {/* Stock status */}
            <div className="product-stock">
              {isInStock ? (
                <span className="in-stock">
                  <FiCheck /> In Stock ({displayProduct.StockQuantity}{" "}
                  available)
                </span>
              ) : (
                <span className="out-of-stock">
                  <FiAlertCircle /> Out of Stock
                </span>
              )}
            </div>

            {/* Feature icons row — driven by real product data */}
            {features.length > 0 && (
              <div className="product-features-row">
                {features.map((feature, idx) => (
                  <div className="feature-item" key={`feat-${idx}`}>
                    <FeatureIcon type={feature.icon} />
                    <div>
                      <strong>{feature.primary}</strong>
                      <span>{feature.secondary}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Tabs + Related Products row */}
        <div className="product-bottom-row">
          {/* LEFT: Tabs */}
          <div className="product-tabs-column">
            <div className="product-tabs">
              <button
                type="button"
                className={`tab-btn ${
                  activeTab === "overview" ? "active" : ""
                }`}
                onClick={() => setActiveTab("overview")}
              >
                Overview
              </button>
              <button
                type="button"
                className={`tab-btn ${
                  activeTab === "specifications" ? "active" : ""
                }`}
                onClick={() => setActiveTab("specifications")}
              >
                Specifications
              </button>
              <button
                type="button"
                className={`tab-btn ${activeTab === "reviews" ? "active" : ""}`}
                onClick={() => setActiveTab("reviews")}
              >
                Reviews ({totalReviews})
              </button>
            </div>

            {activeTab === "overview" && (
              <div className="tab-panel overview-panel">
                <h3>Powerful Sound. All Day Comfort.</h3>
                <p>
                  {product.Description ||
                    "No description available for this product yet."}
                </p>
                {product.Additional && (
                  <>
                    <h3>Additional</h3>
                    <p>{product.Additional}</p>
                  </>
                )}
                <ul className="overview-bullets">
                  <li>
                    <FiCheck /> Premium quality build
                  </li>
                  <li>
                    <FiCheck /> Designed for comfort and durability
                  </li>
                  <li>
                    <FiCheck /> Backed by our satisfaction guarantee
                  </li>
                  <li>
                    <FiCheck /> Fast, reliable nationwide shipping
                  </li>
                </ul>
              </div>
            )}

            {activeTab === "specifications" && (
              <div className="tab-panel specifications-panel">
                <h3>Technical Specifications</h3>
                <table className="specifications-table">
                  <tbody>
                    <tr>
                      <td>Category</td>
                      <td>{product.CategoryName || "General"}</td>
                    </tr>
                    {product.BrandName && (
                      <tr>
                        <td>Brand</td>
                        <td>{product.BrandName}</td>
                      </tr>
                    )}
                    {displayProduct.SKU && (
                      <tr>
                        <td>SKU</td>
                        <td>{displayProduct.SKU}</td>
                      </tr>
                    )}
                    {product.Specification && (
                      <tr>
                        <td>Specification</td>
                        <td>{product.Specification}</td>
                      </tr>
                    )}
                    <tr>
                      <td>Stock</td>
                      <td>{displayProduct.StockQuantity || 0} units</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}

            {activeTab === "reviews" && (
              <div className="tab-panel reviews-panel" ref={reviewSectionRef}>
                <div className="reviews-summary">
                  <div className="summary-rating">
                    <div className="rating-number">
                      {averageRating.toFixed(1)}
                    </div>
                    <div className="rating-stars-large">
                      {renderStars(averageRating, false, "large")}
                    </div>
                    <div className="rating-total">{totalReviews} reviews</div>
                  </div>
                  <div className="rating-bars">
                    {[5, 4, 3, 2, 1].map((star) => {
                      const count = reviews.filter(
                        (r) => Math.floor(r.Rating) === star,
                      ).length;
                      const percentage =
                        totalReviews > 0 ? (count / totalReviews) * 100 : 0;
                      return (
                        <div key={star} className="rating-bar-item">
                          <span className="rating-star">{star} ★</span>
                          <div className="rating-bar">
                            <div
                              className="rating-bar-fill"
                              style={{ width: `${percentage}%` }}
                            ></div>
                          </div>
                          <span className="rating-bar-count">{count}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="reviews-header">
                  <h3>Customer Reviews</h3>
                  {!showReviewForm && !userReview && (
                    <button
                      type="button"
                      className="write-review-btn"
                      onClick={handleWriteReviewClick}
                    >
                      Write a Review
                    </button>
                  )}
                </div>

                {showReviewForm && (
                  <div className="write-review-form">
                    <h4>
                      {editingReview ? "Edit Your Review" : "Write a Review"}
                    </h4>
                    <form onSubmit={handleSubmitReview}>
                      <div className="form-group">
                        <label>Your Rating *</label>
                        {renderStars(reviewRating, true, "large")}
                      </div>
                      <div className="form-group">
                        <label>Your Review *</label>
                        <textarea
                          value={reviewComment}
                          onChange={(e) => setReviewComment(e.target.value)}
                          placeholder="Share your experience with this product..."
                          rows="5"
                          required
                        />
                      </div>
                      <div className="form-actions">
                        <button
                          type="button"
                          className="cancel-btn"
                          onClick={() => {
                            setShowReviewForm(false);
                            setEditingReview(null);
                            setReviewComment("");
                            setReviewRating(5);
                          }}
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          className="submit-btn"
                          disabled={
                            submitReview.isLoading || updateReview.isLoading
                          }
                        >
                          {submitReview.isLoading || updateReview.isLoading
                            ? "Submitting..."
                            : editingReview
                              ? "Update Review"
                              : "Submit Review"}
                        </button>
                      </div>
                    </form>
                  </div>
                )}

                <div className="reviews-list">
                  {reviewsLoading ? (
                    <div className="reviews-loading">Loading reviews...</div>
                  ) : reviews.length === 0 ? (
                    <div className="no-reviews">
                      <p>
                        No reviews yet. Be the first to review this product!
                      </p>
                    </div>
                  ) : (
                    reviews.map((review) => (
                      <div key={review.ReviewID} className="review-card">
                        <div className="review-header">
                          <div className="reviewer-info">
                            <FiUser className="reviewer-icon" />
                            <span className="reviewer-name">
                              {review.ReviewerName}
                            </span>
                          </div>
                          <div className="review-rating">
                            {renderStars(review.Rating, false, "small")}
                          </div>
                          {user && review.UserID === user.userId && (
                            <div className="review-actions">
                              <button
                                type="button"
                                onClick={() => handleEditReview(review)}
                                className="edit-review"
                              >
                                <FiEdit2 />
                              </button>
                              <button
                                type="button"
                                onClick={() =>
                                  handleDeleteReview(review.ReviewID)
                                }
                                className="delete-review"
                              >
                                <FiTrash2 />
                              </button>
                            </div>
                          )}
                        </div>
                        <div className="review-date">
                          <FiClock className="date-icon" />
                          {new Date(review.CreatedAt).toLocaleDateString()}
                        </div>
                        <div className="review-comment">{review.Comment}</div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ─── You May Also Like — Horizontal Slider ─── */}
        <section className="related-products-section">
          <div className="related-products-header">
            <h3>You May Also Like</h3>
            <Link to="/shop" className="related-view-all">
              View all
            </Link>
          </div>

          <RelatedProductsCarousel
            products={relatedProducts}
            loading={relatedProductsLoading}
            addToCart={addToCart}
            renderStars={renderStars}
          />
        </section>

        {/* Mobile Floating Add-to-Cart Bar */}
        <div
          className={`mobile-sticky-footer ${
            !showStickyBarAbove ? "is-hidden" : ""
          }`}
        >
          <div className="mobile-sticky-content">
            <div className="mobile-price"></div>
            <div className="mobile-actions-row">
              <div className="mobile-quantity">
                <button
                  type="button"
                  onClick={() => handleQuantityChange("decrease")}
                  disabled={quantity <= 1}
                >
                  <FiMinus />
                </button>
                <span>{quantity}</span>
                <button
                  type="button"
                  onClick={() => handleQuantityChange("increase")}
                  disabled={
                    !isInStock ||
                    quantity >= (displayProduct.StockQuantity || 10)
                  }
                >
                  <FiPlus />
                </button>
              </div>
              <button
                type="button"
                className="mobile-add-to-cart-btn"
                onClick={handleAddToCart}
                disabled={!isInStock}
              >
                <FiShoppingCart /> Add to Cart
              </button>
              <button
                type="button"
                className="mobile-buy-now-btn"
                onClick={handleBuyNow}
                disabled={!isInStock}
                style={{ backgroundColor: tenant?.PrimaryColor }}
              >
                Buy Now
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Auto Scroll-Up Button — outside .container, fixed position */}
      <button
        type="button"
        className={`scroll-to-top-btn ${
          showScrollTop ? "is-visible" : ""
        } ${footerVisible ? "is-over-footer" : ""}`}
        onClick={handleAutoScrollUp}
        aria-label="Auto scroll to top"
        style={{
          "--tenant-color": tenant?.PrimaryColor || "#2c7da0",
        }}
      >
        <FiArrowUp />
      </button>
    </div>
  );
};

export default ProductDetail;
